import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { alarmGridTheme } from './alarmGridTheme';
import type { ColDef, ICellRendererParams, GridReadyEvent } from 'ag-grid-community';
import { Bell, Clock, Hash, Info, ChevronDown, ChevronUp, BellOff } from 'lucide-react';
import { fetchRawAlarmDetail, ignoreRawAlarm } from '@/api/alarms';
import type { RawAlarmDetail } from '@/api/alarms';
import toast from 'react-hot-toast';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

const SEV: Record<number, { label: string; badge: string }> = {
  1: { label: 'Warning', badge: 'bg-blue-100 text-blue-700' },
  2: { label: 'Minor',   badge: 'bg-amber-100 text-amber-700' },
  3: { label: 'Major',   badge: 'bg-red-100 text-red-700' },
};

function PriorityCell({ value }: ICellRendererParams) {
  const s = SEV[value] ?? { label: 'Unknown', badge: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${s.badge}`}>
      {s.label}
    </span>
  );
}

const COL_DEFS: ColDef<RawAlarmDetail>[] = [
  { field: 'created_on',             headerName: 'Zaman',         width: 150, sort: 'desc' },
  {
    field: 'alarm_priority',
    headerName: 'Öncelik',
    width: 120,
    cellRenderer: PriorityCell,
    comparator: (a, b) => a - b,
  },
  { field: 'alarm_source_name',      headerName: 'Kaynak',        width: 110, filter: true },
  { field: 'alarm_power_plant_name', headerName: 'Santral',       flex: 1,    filter: true },
  { field: 'alarm_inverter_name',    headerName: 'İnverter',      flex: 1,    filter: true },
  { field: 'alarm_string_number',    headerName: 'String',        width: 90,  filter: true },
  { field: 'alarm_code',             headerName: 'Hata Kodu',     width: 120, filter: true },
  { field: 'alarm_description',      headerName: 'Açıklama',      flex: 2,    filter: true, tooltipField: 'alarm_description' },
  { field: 'status_description',     headerName: 'İnverter Durumu', flex: 1,  filter: true },
];

function DataItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border bg-white p-3">
      <div className="flex items-center gap-1.5 text-xs text-gray-500">{icon}<span>{label}</span></div>
      <span className="text-sm font-semibold">{value ?? '--'}</span>
    </div>
  );
}

export default function AlarmAltDetailPage() {
  const [searchParams] = useSearchParams();
  const gridRef = useRef<AgGridReact<RawAlarmDetail>>(null);

  const date    = searchParams.get('date')    ?? '';
  const plant   = searchParams.get('plant')   ?? '';
  const inverter = searchParams.get('inverter') ?? '';
  const code    = searchParams.get('code')    ?? '';
  const priority = Number(searchParams.get('priority') ?? 0);
  const source  = searchParams.get('source')  ?? '';
  const desc    = searchParams.get('desc')    ?? '';

  const [rows, setRows] = useState<RawAlarmDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  useErrorToast(fetchError);
  const [ignoring, setIgnoring] = useState(false);
  const [ignored, setIgnored] = useState(false);
  const [showGrid, setShowGrid] = useState(true);

  useEffect(() => {
    if (!date || !plant || !inverter || !code) { setLoading(false); return; }
    fetchRawAlarmDetail({ date, plant, inverter, code, source })
      .then(setRows)
      .catch((err) => {
        console.error('fetchRawAlarmDetail error:', err);
        setFetchError(err?.response?.data?.error ?? err?.message ?? 'Veri yüklenemedi');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [date, plant, inverter, code, source]);

  const onGridReady = (e: GridReadyEvent) => e.api.sizeColumnsToFit();

  const sev = SEV[priority] ?? { label: 'Unknown', badge: 'bg-gray-100 text-gray-500' };

  // Convert date from DD-MM-YYYY to YYYY-MM-DD for display grouping
  const displayDate = date;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">


        {ignored ? (
          <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-500">
            <BellOff size={15} /> Alarm yoksayıldı
          </span>
        ) : (
          <button
            onClick={async () => {
              if (!date || !plant || !inverter || !code) return;
              if (!(await confirmToast('Bu alarm yoksayılsın mı?'))) return;
              setIgnoring(true);
              try {
                await ignoreRawAlarm({ date, plant, inverter, code });
                setIgnored(true);
              } catch {
                toast.error('İşlem başarısız oldu.');
              } finally {
                setIgnoring(false);
              }
            }}
            disabled={ignoring}
            className="flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-100 disabled:opacity-50 transition-colors"
          >
            <BellOff size={15} />
            {ignoring ? 'Yoksayılıyor...' : 'Alarmı Yoksay'}
          </button>
        )}
      </div>

      {/* Header card */}
      <div className="rounded-2xl bg-white shadow-md p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400">{source}</p>
            <h2 className="text-lg font-semibold">{plant}</h2>
            <p className="text-sm text-gray-500">{inverter}</p>
          </div>
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${sev.badge}`}>
            {sev.label}
          </span>
        </div>

        {/* Main stat */}
        <div className="rounded-lg bg-gray-50 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Bell size={16} className="text-rose-500" />
            <span>Hata Kodu</span>
          </div>
          <span className="text-2xl font-bold">{code}</span>
        </div>

        {/* Detail grid */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <DataItem icon={<Clock size={13} />} label="Tarih"          value={displayDate} />
          <DataItem icon={<Hash size={13} />}  label="Tekrar Sayısı"  value={String(rows.length)} />
          <DataItem icon={<Info size={13} />}  label="Açıklama"       value={desc || rows[0]?.alarm_description} />
        </div>
      </div>

      {/* Detail records */}
      <div className="rounded-2xl bg-white shadow-md p-4 space-y-3">
        <button
          onClick={() => setShowGrid(p => !p)}
          className="flex w-full items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold hover:bg-gray-200"
        >
          <span>Detaylı Kayıtlar ({rows.length})</span>
          {showGrid ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showGrid && (
          <div className="ag-theme-quartz rounded-xl overflow-hidden" style={{ height: 400 }}>
            {loading ? (
              <div className="flex h-full items-center justify-center text-gray-400 text-sm">Yükleniyor...</div>
            ) : fetchError ? (
              <LoadRetry />
            ) : (
              <AgGridReact<RawAlarmDetail>
                modules={[AllEnterpriseModule]}
                ref={gridRef}
                rowData={rows}
                columnDefs={COL_DEFS}
                defaultColDef={{ sortable: true, resizable: true, filter: true }}
                theme={alarmGridTheme}
                onGridReady={onGridReady}
                tooltipShowDelay={300}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
